/* eslint-disable prettier/prettier */
import { useInput } from '@nextui-org/react';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { scrollIntoView } from 'seamless-scroll-polyfill';

import { AddPictureIcon, CloseIcon, ImagePlaceholderIcon, SendIcon } from '../../assets';
import EmptyChatIcon from '../../assets/EmptyChatIcon';
import { useMessagesContext } from '../../contexts/MessagesContext';
import { useSessionContext } from '../../contexts/SessionContext';
import { Organization, RoomType } from '../../types/api.graphql';
import { cancelStorage } from '../../utils/Amplify';
import { dateIsFromToday, dateIsFromYesterday, dateIsLessThanWeekAgo, getDayOfWeekOfDate } from '../../utils/date';
import {
  getImageFromChatMessage,
  getTextFromChatMessage,
  messagesImgsCloseTag,
  messagesImgsOpenTag,
  ReceiverType,
} from '../../utils/messages';
import UsersActivityView from '../../views/listings/ListingsPage/DetailsPage/components/UsersActivityView';
import { ActiveRoomDataType } from '../activity/ActivityPanel/ActivityPanel';
import FileUploader from '../FileUploader';
import { ShimmerText, ShimmerTextWrapper } from '../Shimmer';
import { TwoEntitiesChatHeaderProps } from '../SingleChat/GeneralEntitiesHeader';
import { GroupChatHeader } from '../SingleChat/GroupHeader';
import VehicleAndMemberHeader, { VehicleAndMemberChatHeader } from '../SingleChat/VehicleAndMemberHeader';
import {
  ActionButton,
  BackButtonContainer,
  ChatBody,
  ChatBodyEmptyPlaceholder,
  ChatBodyEmptyPlaceholderSubtitle,
  ChatBodyEmptyPlaceholderTitle,
  ChatEmptyInfoContainer,
  ChatHeader,
  ChatHeaderContent,
  ChatHeaderLeftContainer,
  ChatHeaderTitle,
  ChatImageLoading,
  ChatImagePreview,
  ChatImagePreviewContainer,
  ChatImageProgressBar,
  ChatImageProgressBarFilled,
  ChatInputBox,
  ChatInputButtons,
  CloseButtonContainer,
  Container,
  ConversationDateDivider,
  ConversationTopViewContainer,
  EmptyChatView,
  FooterContainer,
  InputShimmer,
  MessagesContainer,
  MessagesDateBlock,
  MessagesShimmerContainer,
  MobileActionButton,
  MobileSendButton,
  MobileShimmerText,
  NewMessagesPill,
  SendButton,
  StyledBackArrowIcon,
  StyledForm,
  StyledInfiniteScroll,
  StyledMessageBubble,
  StyledTextarea,
  TEXT_AREA_1_ROW_HEIGHT,
} from './SingleChatV2.styles';

export interface ChatEntity {
  id: string;
  type: ReceiverType;
  name?: string;
  imageUrl?: string;
  secondName?: string;
  DefaultImg?: JSX.Element;
}

export type RoomData = ActiveRoomDataType;

interface MessageNode {
  createdAt: any;
  id: string;
  receiverId?: string;
  receiverType?: string;
  sentAt: any;
  text: string;
  sender: {
    id: string;
    firstName?: string;
    email?: string;
    lastName?: string;
    imageUrl?: string;
    organizations?: {
      id: string;
      name?: string;
      imageUrl?: string;
    }[];
  };
  room?: {
    type?: string;
    ownerOrganization?: {
      id: string;
    };
  };
  senderOrganizationId?: string;
}

export type FunctionUserConnected = (userId: string) => boolean;

interface SingleChatProps {
  onSubmit: (message: string) => Promise<void> | undefined;
  onClose: () => void;
  vehicleAndMemberHeader?: VehicleAndMemberChatHeader;
  groupHeader?: GroupChatHeader;
  twoEntitiesHeader?: TwoEntitiesChatHeaderProps;
  messages?: Array<MessageNode>;
  loadMoreMessages?: () => void;
  hasMoreMessages?: boolean;
  loadingMessages?: boolean;
  conversationTopView?: ReactNode;
  isFloatingWindow?: boolean;
  closable?: boolean;
  isShimmer?: boolean;
  isMessageSending?: (sentAt: string) => boolean;
  hasNewMessage?: boolean;
  setHasNewMessage?: (value: boolean) => void;
  roomData?: RoomData;
  roomType?: RoomType;
  isUserConnected?: FunctionUserConnected;
}

const INFINITE_SCROLL_ID = 'infiniteScroll';
const DATE_DIVIDER_CLASS_NAME = 'dateDivider';
const BUBBLE_MESSAGE_HEIGHT = 68;

const ShimmerChatInputBox = () => {
  return (
    <FooterContainer>
     <ChatInputBox>
      <MobileShimmerText $width="2.5rem" $height="2.5rem" $borderRadius="1.25rem" />
      <InputShimmer $flex="1" />
      <ChatInputButtons>
        <ShimmerText $width="2rem" $height="2rem" $borderRadius="1rem" />
        <ShimmerText $width="3.75rem" $height="2rem" $borderRadius="2.125rem" />
      </ChatInputButtons>
    </ChatInputBox>
    </FooterContainer>
  );
};

interface ImagePreviewComponentProps { uploadedImageURL: string; uploadingImage: boolean; uploadingImageProgress: number; handleRemoveImage: () => void; handleCancelImageUpload: () => void; showInMobile?: boolean }

export const ImagePreviewComponent = ({ uploadedImageURL, uploadingImage, uploadingImageProgress, handleRemoveImage, handleCancelImageUpload, ...rest } : ImagePreviewComponentProps) => {
  return (
    <ChatImagePreviewContainer {...rest}>
    {uploadedImageURL && (
      <>
        <ChatImagePreview src={uploadedImageURL} />
        <CloseIcon id="close-icon" onClick={() => handleRemoveImage()} />
      </>
    )}
    {!uploadedImageURL && uploadingImage && (
      <>
        <ChatImageLoading>
          <ImagePlaceholderIcon />
          <ChatImageProgressBar>
            <ChatImageProgressBarFilled percentage={uploadingImageProgress} />
          </ChatImageProgressBar>
        </ChatImageLoading>
        <CloseIcon id="close-icon" onClick={() => handleCancelImageUpload()} />
      </>
    )}
  </ChatImagePreviewContainer>
  )
}

export const ShimmerMessages = () => {
  return (
    <>
      <StyledMessageBubble isLoggedUserSide={false} isShimmer minWidth="80%" />
      <StyledMessageBubble isLoggedUserSide isShimmer minWidth="80%" />
      <StyledMessageBubble isLoggedUserSide={false} isShimmer minWidth="80%" />
    </>
  );
}

const SingleChatV2 = ({
  onSubmit,
  onClose,
  vehicleAndMemberHeader,
  groupHeader,
  twoEntitiesHeader,
  messages,
  loadMoreMessages,
  hasMoreMessages,
  loadingMessages,
  conversationTopView,
  isFloatingWindow = true,
  closable = true,
  isShimmer,
  isMessageSending,
  hasNewMessage,
  setHasNewMessage,
  roomData,
  roomType,
  isUserConnected,
}: SingleChatProps) => {
  // Dependencies
  const { value: newMessage, setValue: setNewMessage, bindings } = useInput('');
  const { me } = useSessionContext();
  const { t } = useTranslation();
  const { isMessageError, reSendMessage, creatingMessage, usersActivity } = useMessagesContext();

  // State
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingImageProgress, setUploadingImageProgress] = useState(0);
  const [uploadedImageURL, setUploadedImageURL] = useState(null);
  const [loadingMessagesStarted, setLoadingMessagesStarted] = useState(false);
  const [hideScroll, setHideScroll] = useState(false);
  const [inputHeight, setInputHeight] = useState(TEXT_AREA_1_ROW_HEIGHT);
  const [firstMessageSending, setFirstMessageSending] = useState<MessageNode>(undefined);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const inputAreaRef = useRef<any>();
  const uploadOperationRef = useRef<any | undefined>(undefined);
  const scrollTimerRef = useRef<any>();

  const messagesLocal = messages ?? (firstMessageSending ? [firstMessageSending] : undefined);

  // Effects
  useEffect(() => {
    if (!loadingMessagesStarted && loadingMessages) {
      setLoadingMessagesStarted(true);
    } else if (loadingMessagesStarted && !loadingMessages) {
      setHideScroll(true);
      setLoadingMessagesStarted(false);
      setTimeout(() => {
        scrollToBottom();
        setHideScroll(false);
      }, 200);
    }
  }, [loadingMessages, loadingMessagesStarted]);

  useEffect(() => {
    const messageBox = document.getElementById(INFINITE_SCROLL_ID);
    const scrollEventFunction = () => {
      const dateDividers = document.getElementsByClassName(DATE_DIVIDER_CLASS_NAME);
      // The setTimeOut will be executed a short time after the user stops scrolling to hide the floating date
      if (scrollTimerRef.current !== null) {
        clearTimeout(scrollTimerRef.current);
      }
      scrollTimerRef.current = null;
      scrollTimerRef.current = setTimeout(function () {
        if (messageBox?.scrollTop !== 0) {
          Array.from(dateDividers).forEach((divider) => {
            const dateDivider = divider as HTMLElement;
            // Hide if the sticky position is more than 0px in MessagesDateBlock
            if (dateDivider.offsetTop !== 0) {
              dateDivider.style.visibility = 'hidden';
            }
          });
        }
      }, 200);
      Array.from(dateDividers).forEach((divider) => {
        const dateDivider = divider as HTMLElement;
        dateDivider.style.visibility = 'visible';
      });
    };
    messageBox?.addEventListener?.('scroll', scrollEventFunction);

    return () => messageBox?.removeEventListener?.('scroll', scrollEventFunction);
  }, []);

  useEffect(() => {
    if (!creatingMessage && firstMessageSending && messages) {
      setFirstMessageSending(undefined);
    }
  }, [creatingMessage, firstMessageSending, messages]);
  
  // Helpers
  const renderDate = (date: string) => {  
    const dateValue = new Date(date);
    const momentDate = moment(date);

    switch (true) {
      case dateIsFromToday(dateValue):
        return t('components.chat.today')
      case dateIsFromYesterday(dateValue):
        return t('components.chat.yesterday')
      case dateIsLessThanWeekAgo(dateValue):
        return getDayOfWeekOfDate(dateValue);
      default:
        return momentDate.format('MM/DD/YYYY');
    }
  };
  
  const renderMessagesBlock = (messages: MessageNode[], roomData: RoomData) => {
    const getMessageOrganizationAvatar = (message: MessageNode): Organization | undefined => {
      const senderOrganizationId = message.senderOrganizationId;
      const entity1 = roomData?.entity1;
      const entity2 = roomData?.entity2;
      if (!senderOrganizationId) return undefined;
      if (entity1?.id === senderOrganizationId && entity1?.type === ReceiverType.Organization) {
        return entity1;
      } else {
        return entity2;
      }
    }

    const isLoggedUserSide = (message: MessageNode): boolean => {
      const senderOrganizationId = message.senderOrganizationId;
      const entity1 = roomData?.entity1;
      if (roomType !== RoomType.ListingInternalRoom) {
        // Note: entity1 always refer to the logged user
        if (senderOrganizationId) {
          return entity1?.id === senderOrganizationId; 
        }
        if (entity1?.type === ReceiverType.Organization) {
          return false;
        }
      }
      return me?.id === message?.sender?.id;
    };

    return messages?.map((message) => (
      <>
        <StyledMessageBubble
          user={{
            name: message.sender?.firstName,
            lastName: message.sender?.lastName,
            profileImage: message.sender?.imageUrl,
            organization: getMessageOrganizationAvatar(message),
          }}
          message={{
            text: getTextFromChatMessage(message.text),
            timestamp: message.createdAt,
            image: getImageFromChatMessage(message.text),
            sentAt: message.sentAt
          }}
          isGroupChat={groupHeader || vehicleAndMemberHeader ? true : false}
          isLoggedUserSide={isLoggedUserSide(message)}
          isSending={!!firstMessageSending || isMessageSending(message.sentAt)}
          isError={isMessageError(message.sentAt)}
          isUserConnected={usersActivity?.some(user => user.userId === message.sender?.id && user.isConnected) || isUserConnected(message.sender?.id)}
          onResend={reSendMessage}
        />
      </>
    ));
  };

  const [newMsgInScrollRange, setNewMsgInScrollRange] = useState(false);
  const onScroll = (event: any) => {
    const { scrollHeight, scrollTop, clientHeight } = event.target;
    const scroll = scrollHeight - scrollTop - clientHeight;
    if (scroll > BUBBLE_MESSAGE_HEIGHT) {
      // if it scrolls up more than 1 message height then show bubble
      setNewMsgInScrollRange(true);
    } else {
      // if user scrolls down manually and doesn't click the bubble then remove it
      setNewMsgInScrollRange(false);
      setHasNewMessage?.(false);
    }
  };

  const scrollToBottom = (smooth?: boolean) => {
    if (messagesEndRef?.current) {
      const defaultScrollOptions: any = { block: 'nearest', inline: 'start' };
      const scrollOptions: any = smooth ? { ...defaultScrollOptions, behavior: 'smooth' } : defaultScrollOptions;
      scrollIntoView(messagesEndRef.current, scrollOptions);
    }
  };

  // Handlers
  const handleUploadProgressCallback = (progress) => {
    setUploadingImageProgress(100 * (progress.loaded / progress.total));
  };

  const handleUploadImageClick = () => {
    if (!uploadedImageURL && !uploadingImage) {
      fileInputRef?.current.click();
    } else {
      toast.error(t('components.chat.oneImageUploadError'));
    }
  };

  const handleUploadFinish = (url: string) => {
    setUploadedImageURL(url);
    setUploadingImageProgress(0);
  };

  const handleRemoveImage = () => {
    fileInputRef.current.value = ''; // Reset the input's value
    setUploadedImageURL(null);
    setUploadingImage(false);
  };

  const handleCancelImageUpload = () => {
    void (async () => {
      await cancelStorage(uploadOperationRef?.current);
      fileInputRef.current.value = ''; // Reset the input's value
      setUploadedImageURL(null);
      setUploadingImage(false);
      setUploadingImageProgress(0);
    })();
  };

  const handleSubmit = () => {
    let imageText = '';
    let messageText = '';
    if (uploadedImageURL && !uploadingImage) {
      imageText = `${messagesImgsOpenTag}${uploadedImageURL}${messagesImgsCloseTag}`;
    }
    if (newMessage !== '') {
      messageText = newMessage;
    }
    if (!messages) {
      setFirstMessageSending({ 
        id: '-1',
        createdAt: new Date(),
        sentAt: new Date(),
        room: {
          type: roomData.type,
        },
        sender: me,
        senderOrganizationId: roomData?.entity1?.id,
        text: messageText,
      });
    }
    void onSubmit([imageText, messageText].join(''));
    setNewMessage('');
    inputAreaRef.current.style.height = '3.625rem'; // Original size of the textarea w/minRows={2}
    setUploadedImageURL(null);
    setTimeout(() => {
      scrollToBottom();
    });
  };

  const handleTextAreaKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && e.shiftKey === false) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChatCloseClick = () => {
    onClose?.();
  };

  const showNewMessages = () => {
    scrollToBottom(true);
    setHasNewMessage(false);
  };

  // Derived State
  const groupedDays = (messages: MessageNode[]): Record<string, MessageNode[]> => {
    return messages?.reduce((acc, el) => {
      const messageDay = renderDate(el.createdAt);
      if (acc[messageDay]) {
        return { ...acc, [messageDay]: acc[messageDay].concat([el]) };
      }
      return { ...acc, [messageDay]: [el] };
    }, {});
  };

  const groupedMessages = useMemo(() => {
    return messagesLocal ? groupedDays(messagesLocal) : {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesLocal]);

  const handleInputHeightChange = (height: number) => {
    setInputHeight(height/16);
  }

  const isLoading = isShimmer || loadingMessages;
  const chatWithNonexistentEntity = roomData?.entity2 && !roomData?.entity2?.id;
  const shouldShowEmptyView = !isLoading && (!messagesLocal || messagesLocal.length === 0);
  const shouldShowShimmer = isLoading && !firstMessageSending;
  // hide users activity for an internal room
  const shouldShowUsersActivity = roomData?.type !== RoomType.ListingInternalRoom;
  // show 4 avatar shimmers for organizatins rooms and 1 for user ones
  const shimmersCount = roomData?.entity2?.type === ReceiverType.User || chatWithNonexistentEntity ? 1 : 4;

  return (
    <Container $isFloatingWindow={isFloatingWindow}>
      <ChatHeader>
        <BackButtonContainer onClick={onClose}>
          <StyledBackArrowIcon />
        </BackButtonContainer>
        <ChatHeaderContent>
        <VehicleAndMemberHeader data={vehicleAndMemberHeader} />
        <ShimmerTextWrapper loading={shouldShowShimmer} $width="9rem" $flex="1">
          <ChatHeaderLeftContainer>
            <ChatHeaderTitle>{roomData?.title}</ChatHeaderTitle>
            {chatWithNonexistentEntity && twoEntitiesHeader?.subtitle}
          </ChatHeaderLeftContainer>
          </ShimmerTextWrapper>
          {shouldShowUsersActivity && 
            <UsersActivityView 
              loading={shouldShowShimmer || !usersActivity} 
              users={usersActivity || []} 
              maxAvatarsCount={3} 
              shimmersCount={shimmersCount} 
              tooltipPlacement="bottomEnd" 
            />
          }
        </ChatHeaderContent>
        {closable && (
          <CloseButtonContainer onClick={handleChatCloseClick}>
            <CloseIcon />
          </CloseButtonContainer>
        )}
      </ChatHeader>
      {conversationTopView && <ConversationTopViewContainer>{conversationTopView}</ConversationTopViewContainer>}
      <ChatBody $hasMoreMessages={hasMoreMessages} $hideScroll={hideScroll} onScroll={onScroll} id={INFINITE_SCROLL_ID}>
        {shouldShowEmptyView && (
          <EmptyChatView>
            <ChatBodyEmptyPlaceholder>
            <EmptyChatIcon />
            <ChatEmptyInfoContainer>
              <ChatBodyEmptyPlaceholderTitle>
                {chatWithNonexistentEntity ? t('components.chat.emptyChatTitleWithoutUser'): t('components.chat.emptyChatPlaceholderTitle')}
              </ChatBodyEmptyPlaceholderTitle>
              <ChatBodyEmptyPlaceholderSubtitle>
                {chatWithNonexistentEntity ? t('components.chat.emptyChatSubtitleWithoutUser') : t('components.chat.emptyChatPlaceholderSubtitle')}
              </ChatBodyEmptyPlaceholderSubtitle>
            </ChatEmptyInfoContainer>
            </ChatBodyEmptyPlaceholder>
          </EmptyChatView>
        )}
        {shouldShowShimmer ? ( <MessagesShimmerContainer><ShimmerMessages /></MessagesShimmerContainer>) : messagesLocal && (
          <StyledInfiniteScroll
            initialLoad={false}
            loadMore={loadMoreMessages}
            hasMore={hasMoreMessages}
            useWindow={false}
            isReverse={true}
            loader={<ShimmerMessages />}
            threshold={250}
          >
            <MessagesContainer>
              {Object.keys(groupedMessages).map((key) => {
                return (
                  <MessagesDateBlock>
                    {key.length > 0 && (
                      <ConversationDateDivider className={DATE_DIVIDER_CLASS_NAME}>{key}</ConversationDateDivider>
                    )}
                    {renderMessagesBlock(groupedMessages[key], roomData)}
                  </MessagesDateBlock>
                );
              })}
              <div ref={messagesEndRef} />
            </MessagesContainer>
          </StyledInfiniteScroll>
        )}
      </ChatBody>
      {shouldShowShimmer ? (<ShimmerChatInputBox />) : 
      (<FooterContainer> 
        <ImagePreviewComponent
          uploadedImageURL={uploadedImageURL}
          uploadingImage={uploadingImage}
          uploadingImageProgress={uploadingImageProgress}
          handleRemoveImage={handleRemoveImage}
          handleCancelImageUpload={handleCancelImageUpload}
        />
        <ChatInputBox>
        <MobileActionButton onClick={handleUploadImageClick}>
          <FileUploader
            inputRef={fileInputRef}
            setLoading={setUploadingImage}
            onUploadFinish={handleUploadFinish}
            accept="image/*"
            uploadProgressCallback={handleUploadProgressCallback}
            uploadOperationRef={uploadOperationRef}
          />
          <AddPictureIcon />
        </MobileActionButton>
        <StyledForm onSubmit={handleSubmit}>
          <StyledTextarea
            {...bindings}
            ref={inputAreaRef}
            id="text-area"
            aria-label="chat-text-area"
            placeholder={t('components.chat.chatInputPlaceholder')}
            fullWidth
            value={newMessage}
            animated={false}
            shadow={false}
            minRows={1}
            maxRows={3}
            onChange={(e: any) => setNewMessage(e.target.value)}
            onKeyDown={handleTextAreaKeyDown}
            onHeightChange={handleInputHeightChange}
            $hasMoreThanOneRow={inputHeight > TEXT_AREA_1_ROW_HEIGHT}
          />
        </StyledForm>
        <ChatInputButtons>
        <ActionButton onClick={handleUploadImageClick}>
          <FileUploader
            inputRef={fileInputRef}
            setLoading={setUploadingImage}
            onUploadFinish={handleUploadFinish}
            accept="image/*"
            uploadProgressCallback={handleUploadProgressCallback}
            uploadOperationRef={uploadOperationRef}
          />
            <AddPictureIcon />
          </ActionButton>
          <SendButton onPress={() => handleSubmit()} disabled={!uploadedImageURL && newMessage === ''}>
          {t('components.chat.sendButton')}
        </SendButton>
        </ChatInputButtons>
        <NewMessagesPill $visible={hasNewMessage && newMsgInScrollRange} onClick={showNewMessages}>{t('components.chat.newMessages')}</NewMessagesPill>
        <MobileSendButton onTouchEnd={() => handleSubmit()} $visible={uploadedImageURL || newMessage.length > 0} autoFocus>
          <SendIcon />
        </MobileSendButton>
      </ChatInputBox>
     </FooterContainer>)}
    </Container>
  );
};

export default SingleChatV2;
